// @flow
import moment from 'moment';
import { get } from 'lodash';

import {
  getIdsFromProducts as pertimmGetIdsFromProducts,
  searchWithFacets as pertimmSearchWithFacets,
} from 'Utils/pertimm-client';
import resortThumbnailQuery, {
  getPriceFields,
  cleanupArgs,
} from 'Utils/resort-thumbnail-query/resortThumbnailQuery';
import gqlClient from 'Utils/graphql-client/graphqlClient';
import {
  STATE_SEARCH_PRODUCT_CONFIG,
  STATE_SEARCH_PRODUCTS,
  STATE_SEARCH_PRODUCT_PRICE,
  STATE_SEARCH_PRODUCT_CHILDCARE,
} from 'Utils/request/requestDictionary';
import { DEPARTURE_CITY_FACET_ID, DATE_FORMAT } from 'Utils/faceting/constants';
import { formatTimestampDate } from 'Utils/date/dateUtils';
import { isSeller } from 'Utils/seller/sellerUtils';

const PERTIMM_ENGLISH_SPEAKING_LOCALE = 'en';

export function formatPertimmDate(departureDate: moment, appContext: AppContext): string {
  const format =
    get(appContext, 'locale', '').slice(0, 2) === PERTIMM_ENGLISH_SPEAKING_LOCALE
      ? 'YYYY/MM/DD'
      : 'DD/MM/YYYY';
  return departureDate.isValid() ? departureDate.format(format) : '';
}

function getResortThumbnailArgsConfig(appContext: AppContext): Promise<*> {
  const query = `query {
    config {
      thumbnails {
        tourDurationPlaceholder
        cruiseTimingPlaceholder
        cruiseDurationPlaceholder
        dateFormat
      }
    }
  }`;
  return gqlClient.runQuery(query, appContext, { requestName: STATE_SEARCH_PRODUCT_CONFIG });
}

type GetAllProductsInfoParams = {
  appContext: AppContext,
  options?: FacetingSearchOptions,
  ids?: Array<string>,
};
function getAllProductsInfo({ appContext, options = {}, ids = [] }: GetAllProductsInfoParams) {
  const { cmsContent, cmsRelease } = appContext;
  const { offerId = '', offerName = '', productIds = [] } = options;

  return getResortThumbnailArgsConfig(appContext).then(data => {
    const resortThumbnailQueryArgs = {
      content: cmsContent,
      release: cmsRelease,
      offerName,
      offerId,
      ...data.config.thumbnails,
    };

    const allProductsId = productIds.length ? ids.filter(id => productIds.includes(id)) : ids;

    const query = `query {
      resorts(resortIds : ["${allProductsId.join('","')}"]) {
        ${resortThumbnailQuery(resortThumbnailQueryArgs, { withoutPrice: true })}
      }
    }`;
    return gqlClient.runQuery(query, appContext, { requestName: STATE_SEARCH_PRODUCTS });
  });
}

type LoadProductsParams = {
  appContext: AppContext,
  options?: FacetingSearchOptions,
};
export function loadProducts({ appContext, options }: LoadProductsParams) {
  return pertimmGetIdsFromProducts(appContext).then(ids => {
    return getAllProductsInfo({ appContext, options, ids });
  });
}

type LoadProductsWithIdsParams = {
  appContext: AppContext,
  productIds: Array<string>,
};
export function loadProductsWithIds({ appContext, productIds }: LoadProductsWithIdsParams) {
  return getAllProductsInfo({ appContext, options: {}, ids: productIds });
}

type SearchWithFacetsParams = {
  criteria: FacetingSearchCriteria,
  options: FacetingSearchOptions,
  appContext: AppContext,
};
export function searchWithFacets({
  criteria,
  options,
  appContext,
}: SearchWithFacetsParams): Promise<*> {
  const productsPromise = loadProducts({ appContext, options });
  const departureDate = formatPertimmDate(moment(criteria.departureDate), appContext);
  const userIsSeller = isSeller(appContext);
  // $FlowFixMe
  const searchPromise = pertimmSearchWithFacets({
    criteria: {
      ...criteria,
      departureDate,
      tripDuration: userIsSeller ? null : criteria.tripDuration,
    },
    appContext,
  });
  return Promise.all([productsPromise, searchPromise]).then(([productsInfo, pertimmResults]) => {
    const filteredProducts = get(pertimmResults, 'answers', []).reduce((acc, result) => {
      const resultProductId = get(result, 'meta.product_id[0]');
      const resort = productsInfo.resorts.find(({ id }) => id === resultProductId);
      return resort ? [...acc, resort] : acc;
    }, []);

    const sortedFilteredProducts =
      criteria.productIds && criteria.productIds.length > 0
        ? criteria.productIds.map(id => filteredProducts.find(product => product.id === id))
        : filteredProducts;

    return {
      ...pertimmResults,
      query: criteria.query || '',
      search: { products: sortedFilteredProducts.filter(Boolean) },
    };
  });
}

type TransportArgs = {
  departureCity: string,
  forceWithoutTransport: boolean,
};
function getTransportArgs(criteria): TransportArgs {
  const departureCityFilter = get(criteria, 'filters', []).find(
    filter => filter.id === DEPARTURE_CITY_FACET_ID,
  );
  const selectedDepartureCity = get(departureCityFilter, 'name');
  switch (selectedDepartureCity) {
    case 'NO':
      return { forceWithoutTransport: true, departureCity: '' };
    case undefined:
      return { forceWithoutTransport: false, departureCity: '' };
    default:
      return { forceWithoutTransport: false, departureCity: selectedDepartureCity };
  }
}

export type FacetingPricesCriteria = {
  boutique?: string,
  defaultDepartureCity?: string,
  departureDate: number,
  duration: number,
  filters: Array<{ id: string, name: string }>,
  maxDate?: number,
  minDate?: number,
  offerId?: string,
  offerName?: string,
  productIds: Array<string>,
  query: string,
};

type LoadPricesParams = {
  appContext: AppContext,
  criteria: FacetingPricesCriteria,
};

export function loadPrices({ appContext, criteria = {} }: LoadPricesParams) {
  const { offerName = '', offerId = '', productIds = [], duration } = criteria;
  const { departureCity, forceWithoutTransport } = getTransportArgs(criteria);
  const date = formatTimestampDate(criteria.departureDate, DATE_FORMAT) || '';
  const dateStart = formatTimestampDate(criteria.minDate, DATE_FORMAT) || '';
  const dateEnd = formatTimestampDate(criteria.maxDate, DATE_FORMAT) || '';

  const hasDateRange = dateStart && dateEnd && !date;

  const query = `
    query {
      resorts(
        optimizedPriceCalls: true,
        ${date ? `date: "${date}",` : ''}
        ${hasDateRange ? `dateStart: "${dateStart}",` : ''}
        ${hasDateRange ? `dateEnd: "${dateEnd}",` : ''}
        resortIds: ["${productIds.join('","')}"],
        departureCity: "${departureCity}",
        duration: ${String(duration || null)},
        offerName: "${offerName}",
        offerId: "${offerId}",
        forceWithoutTransport: ${String(forceWithoutTransport)},
      ) {
        id
        ${getPriceFields(
          cleanupArgs({
            optimizedPriceCalls: true,
            resortIds: productIds,
            departureCity,
            duration: duration || undefined, // for flow: criteria duration can be null, but cleanupArgs doesn't accept null as parameter.
            offerName,
            offerId,
            forceWithoutTransport,
            content: appContext.cmsContent,
            release: appContext.cmsRelease,
            date,
            dateStart,
            dateEnd,
          }),
        )}
      }
    }
  `;

  return gqlClient.runQuery(query, appContext, { requestName: STATE_SEARCH_PRODUCT_PRICE });
}

export type FacetingChildcareCriteria = {
  departureDate: number,
  duration: number,
  kids: Array<{
    birthdate?: timestamp,
    childcareIncluded: boolean,
  }>,
  productIds: Array<string>,
};

type LoadChildcareParams = {
  appContext: AppContext,
  criteria: FacetingChildcareCriteria,
};

export function loadChildcareAvailabilities({ appContext, criteria = {} }: LoadChildcareParams) {
  const { productIds = [], departureDate: arrivalDate, duration, kids } = criteria;

  if (!kids || kids.length === 0) {
    return Promise.resolve();
  }

  const query = `
    query {
      childcareSearch(
        arrival_date: "${formatTimestampDate(arrivalDate, 'YYYYMMDD') || ''}",
        product_ids: ["${productIds.join('","')}"],
        concerned_attendees_birthdates: ["${kids
          .map(({ birthdate }) => formatTimestampDate(birthdate, 'YYYYMMDD'))
          .join('","')}"],
        duration: ${String(duration || null)},
      ) {
        id
        productId,
        availability,
        isIncluded,
        ageMin,
        ageMax
        remainingStock
      }
    }
  `;

  return gqlClient.runQuery(query, appContext, {
    requestName: STATE_SEARCH_PRODUCT_CHILDCARE,
  });
}
